import { Component, OnInit, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { BasePage } from '../../../core/base-page';
import { ContentPreviewComponent } from '../content-preview/content-preview.component';
import { DomSanitizer } from '@angular/platform-browser';
import { Upload } from '../../data/schema/upload';
import { AuthenticationService } from '../../modules/auth-core/data/service/authentication.service';
import { ServiceHelper } from '../../helper/services.helper';
import { Log } from '../../log';
import { AppSettings } from '../../configs/app.settings';

@Component({
  selector: 'app-content-upload',
  templateUrl: './content-upload.component.html',
  styleUrls: ['./content-upload.component.scss'],
})
export class ContentUploadComponent extends BasePage implements OnInit {

  @ViewChild('viewer', { static: true }) contentViewer?: ContentPreviewComponent;
  @Output() fileSelected: EventEmitter<Upload> = new EventEmitter();
  @Output() fileRemoved: EventEmitter<Upload> = new EventEmitter();

  @Input() files: Upload[] = [];
  @Input() showUploadButtonText?: boolean;
  @Input() config?: IUploadConfig;

  @Input() recommendedSize: string;
  @Input() recommendFileTypes: string;

  filesRemaining = 0;
  id: string = '';
  lastFile: Upload = new Upload();
  libraryShown = false;

  private defaultConfig: IUploadConfig = {
    maxFiles: 1,
    allowChange: true,
    showPreview: true,
    acceptedTypes: 'image/*',
  }

  constructor(
    authService: AuthenticationService,
    private sanitizer: DomSanitizer) {
    super(authService);
    Log.debug('content upload')
  }

  ngOnInit() {
    this.id = this.getuploadId();

    // set the config
    this.config = Object.assign(this.defaultConfig, this.config);

    this.filesRemaining = (this.config.maxFiles != null ? this.config.maxFiles - this.files.length : 1);
  }

  reset() {
    this.filesRemaining = this.config?.maxFiles ?? 1;
    this.files = [];
  }

  removeFile(file: Upload) {
    this.fileRemoved.emit(file);
    this.filesRemaining++;
    this.lastFile = new Upload();
  }

  triggerBrowse() {
    this.libraryShown = false;
    document.getElementById(this.id)?.click();
  }

  showLibrary() {
    this.libraryShown = !this.libraryShown;
  }

  async selectFile(selFile: any) {
    if (!this.canAddMoreMedia) {
      return;
    }
    Log.debug('selectAsset', selFile.target.files);

    // Verify we can add pictures (give warnings)
    if (selFile != null && selFile.target.files.length > 0) {
      for (const file of selFile.target.files) {
        const blob = file as Blob;
        const sizeInMb = Math.floor((blob.size / 1024) / 1024);

        // basic size check, video, doc, etc is 100MB, image is set in app settings
        const maxSize = (this.isImage(file.name) ? AppSettings.maxImageSizeMB : 100);
        if (sizeInMb > maxSize) {
          ServiceHelper.displayAlert('File too big', `Cannot upload file. Size too large: ${sizeInMb}MB. Max ${maxSize}MB allowed.`);
        } else {
          // let path = '';
          // if (this.isImage(file.name)) {
          //   path = await Utilities.resizeImage(blob, 1600, 1200) as string;
          // } else {
          //   path = URL.createObjectURL(file);
          // }
          this.prepareForUpload(URL.createObjectURL(file), file.name, file.type);
        }
      }
    }
  }

  private isImage(fileName: string) {
    const images: string[] = ['jpg', 'bmp', 'png', 'svg', 'jpeg'];
    const split = fileName.split('.');
    const extension = split[split.length - 1].toLowerCase();
    return (images.indexOf(extension) > -1);
  }

  private prepareForUpload(path: string, name: string, type: string) {
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(path);
    this.lastFile = new Upload(path);
    this.lastFile.safeUrl = safeUrl;
    this.lastFile.mimeType = type;
    this.lastFile.thumbPath = safeUrl;
    this.lastFile.filename = name;
    this.fileLoaded(this.lastFile);
  }

  private canAddMoreMedia() {
    if (this.filesRemaining === 0 || this.config?.allowChange) {
      ServiceHelper.displayAlert('Too Many Files', `You can only select up to ${this.config?.maxFiles} files.`);
      return false;
    }
    return true;
  }

  private fileLoaded(file: Upload) {
    // emit that we loaded it
    this.fileSelected.emit(file);
    if (this.config?.maxFiles != null && this.files.length + 1 > this.config.maxFiles) {
      this.files.shift(); // remove the first item to allow new item to be added
    }
    this.files.push(file);
    this.files = Object.assign([], this.files);
    this.filesRemaining--;
    if (this.config?.showPreview) {
      this.contentViewer?.imageAdded();
    } else {
      this.lastFile = new Upload();
    }
  }

  private getuploadId() {
    let text = '';
    const possible = 'abcdefghijklmnopqrstuvwxyz1234679';
    for (let i = 0; i < 3; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

}

export interface IUploadConfig {
  showPreview?: boolean;
  maxFiles?: number;
  allowVideoCapture?: boolean;
  allowChange?: boolean;
  fields?: string[];
  acceptedTypes?: string;
  showThumbnails?: boolean;
  allowRemove?: boolean;
  previewHeight?: number;
  thumbSize?: number;
  uploadTitle?: string;
}
